import "./socialLayer.css";
import type { FriendEntry, InviteResult, PartyMemberView, PartyResult } from "@golazo/shared";
import { t } from "../i18n/index.js";
import type { DirectMessages } from "../online/directMessages.js";
import type { FriendsStore, RoomInvite } from "../online/friends.js";
import type { PartyInvite, PartyStore } from "../online/party.js";
import { escapeHtml } from "./careerText.js";
import { vsChip, whereText } from "./friendText.js";
import { copyText, nudgeText, shareText } from "./share.js";
import { tierMarkHtml } from "./tierMark.js";

/**
 * Los amigos, en cualquier pantalla.
 *
 * Todo lo social vivia dentro del lobby: una invitacion que llegaba con el
 * jugador en el menu, en sus estadisticas o en la carrera se perdia sin que
 * nadie la viera, y para saber si habia algun amigo conectado habia que entrar
 * a mirar. Jugar con amigos es la idea del juego: esto lo saca fuera.
 *
 * Dos piezas:
 * - AVISOS arriba, con boton: "Mike te invita · Unirse". En el partido no (ahi
 *   solo se lee, ver `main.ts`): las invitaciones que llegan jugando se
 *   guardan y salen con su boton en la pantalla de resultados.
 * - El PANEL de amigos, que se abre desde el menu: quien esta, donde, y el
 *   boton que toca para cada uno (unirse, jugar, avisar).
 *
 * Y con la pestaña en segundo plano, el titulo parpadea y suena un aviso: si
 * no, una invitacion con la pestaña escondida no la ve nadie.
 */
export type SocialWhere = "menu" | "lobby" | "match" | "results";

export interface SocialActions {
  /** Entrar en esa sala (de un amigo, o la de una invitacion). */
  join(roomId: string): void;
  /** Montar una sala para amigos e invitarle. */
  playWith(clientId: string): void;
  openDm(clientId: string): void;
  openCard(clientId: string, nickname: string): void;
  /** Sala para amigos nueva con el enlace a la vista, para pasarlo fuera. */
  inviteNew(): void;
  /** El lider de tu grupo ha entrado en una sala: ir con el. */
  follow(roomId: string): void;
}

export interface SocialLayerOptions {
  friends: FriendsStore;
  dms?: DirectMessages;
  /** Tu grupo (la escuadra). Sin el, "Invitar" monta una sala en su lugar. */
  party?: PartyStore;
  where(): SocialWhere;
  online(): boolean;
  actions: SocialActions;
  root?: HTMLElement;
}

export interface SocialSummary {
  online: number;
  /** Invitaciones, solicitudes y privados sin leer. */
  pending: number;
  /** Una frase de quien esta: "Mike juega · Ana conectada". Vacia si nadie. */
  line: string;
  /** Tu grupo, si estas en uno: los demas, y si mandas tu. */
  party: { mates: PartyMemberView[]; leader: boolean; leaderId: string; leaderName: string; max: number } | null;
}

export interface SocialLayer {
  openPanel(): void;
  closePanel(): void;
  panelOpen(): boolean;
  summary(): SocialSummary;
  subscribe(fn: () => void): () => void;
  /** Tras el partido: las invitaciones que llegaron jugando, ahora con boton. */
  flushDeferred(): void;
  /** Un aviso cualquiera (lo usa el resto del juego para hablar el mismo idioma). */
  notify(text: string, tone?: "info" | "error"): void;
  dispose(): void;
}

const SOUND_KEY = "fynta.social.sound";
/** Un "se ha conectado" por amigo cada tanto: si entra y sale, no es noticia cada vez. */
const ONLINE_REPEAT_MS = 20 * 60_000;
const MAX_BANNERS = 3;

interface BannerAction {
  label: string;
  run: () => void;
  primary?: boolean;
}

interface BannerSpec {
  /** Uno por clave: si llega otro igual, sustituye al que habia. */
  key: string;
  icon: string;
  html: string;
  actions?: BannerAction[];
  ms: number;
  tone?: "info" | "error" | "invite";
}

function soundOn(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== "0";
  } catch {
    return true;
  }
}

function setSound(on: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, on ? "1" : "0");
  } catch {
    // Sin almacenamiento dura lo que la pestaña.
  }
}

/** Nombres de los que estan: "Mike", "Mike y Ana", "Mike, Ana y 3 más". */
function namesText(names: string[]): string {
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return t("{a} y {b}", { a: names[0]!, b: names[1]! });
  return t("{a}, {b} y {n} más", { a: names[0]!, b: names[1]!, n: names.length - 2 });
}

export function createSocialLayer(opts: SocialLayerOptions): SocialLayer {
  const { friends, dms, actions, party } = opts;
  const root = opts.root ?? document.body;

  // ------------------------------------------------------------ avisos
  const stack = document.createElement("div");
  stack.className = "sl-banners";
  stack.setAttribute("aria-live", "polite");
  root.appendChild(stack);
  const timers = new Map<string, number>();

  const dropBanner = (key: string): void => {
    window.clearTimeout(timers.get(key));
    timers.delete(key);
    const el = stack.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`);
    if (!el) return;
    el.classList.remove("is-in");
    window.setTimeout(() => el.remove(), 220);
  };

  const banner = (spec: BannerSpec): void => {
    // Jugando no se pinta nada encima del campo: ver la cabecera.
    if (opts.where() === "match") return;
    dropBanner(spec.key);
    const el = document.createElement("div");
    el.className = `sl-banner${spec.tone ? ` is-${spec.tone}` : ""}`;
    el.dataset.key = spec.key;
    el.setAttribute("role", spec.tone === "error" ? "alert" : "status");
    el.innerHTML = `
      <span class="sl-banner-icon" aria-hidden="true">${spec.icon}</span>
      <span class="sl-banner-text">${spec.html}</span>
      <span class="sl-banner-actions"></span>
      <button type="button" class="sl-banner-x" aria-label="${escapeHtml(t("Cerrar"))}">✕</button>
      <i class="sl-banner-timer" style="animation-duration:${spec.ms}ms" aria-hidden="true"></i>`;
    const box = el.querySelector(".sl-banner-actions") as HTMLElement;
    for (const a of spec.actions ?? []) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `sl-btn${a.primary ? " is-yes" : ""}`;
      b.textContent = a.label;
      b.addEventListener("click", () => {
        dropBanner(spec.key);
        a.run();
      });
      box.appendChild(b);
    }
    el.querySelector(".sl-banner-x")!.addEventListener("click", () => dropBanner(spec.key));
    stack.prepend(el);
    // Tres a la vez como mucho: el cuarto empuja fuera al mas viejo.
    const all = [...stack.querySelectorAll<HTMLElement>(".sl-banner")];
    for (const old of all.slice(MAX_BANNERS)) dropBanner(old.dataset.key ?? "");
    requestAnimationFrame(() => el.classList.add("is-in"));
    timers.set(
      spec.key,
      window.setTimeout(() => dropBanner(spec.key), spec.ms),
    );
  };

  // ------------------------------------------------------------ llamar la atencion
  let audio: AudioContext | null = null;
  // El contexto de audio solo arranca tras un gesto: se crea en el primero, y
  // desde ahi puede sonar aunque la pestaña este detras.
  const unlockAudio = (): void => {
    if (audio) return;
    try {
      audio = new AudioContext();
    } catch {
      audio = null;
    }
  };
  window.addEventListener("pointerdown", unlockAudio, { once: true, capture: true });
  window.addEventListener("keydown", unlockAudio, { once: true, capture: true });

  const ping = (): void => {
    if (!audio || !soundOn()) return;
    try {
      if (audio.state === "suspended") void audio.resume();
      const now = audio.currentTime;
      // Dos notas cortas, hacia arriba: "¡eh!", no una alarma.
      for (const [freq, at] of [
        [784, 0],
        [1175, 0.09],
      ] as const) {
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + at);
        gain.gain.exponentialRampToValueAtTime(0.12, now + at + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.22);
        osc.connect(gain).connect(audio.destination);
        osc.start(now + at);
        osc.stop(now + at + 0.25);
      }
    } catch {
      // Sin audio no pasa nada: el aviso se ve igual.
    }
  };

  const baseTitle = document.title || "FYNTA";
  let unseen = 0;
  const attention = (text: string, loud: boolean): void => {
    if (document.hidden) {
      unseen += 1;
      document.title = `(${unseen}) ${text}`;
      ping();
    } else if (loud) {
      ping();
    }
  };
  // Vuelve el titulo al volver a la pestaña, o al tocar algo en ella (hay
  // navegadores que no avisan del cambio de pestaña si la ventana ya tenia foco).
  const onVisibility = (): void => {
    if (!document.hidden && unseen > 0) {
      unseen = 0;
      document.title = baseTitle;
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pointerdown", onVisibility, true);

  // ------------------------------------------------------------ estado
  const listeners = new Set<() => void>();
  // Aqui arriba y no con el panel: `friends.subscribe` avisa en el acto, y ese
  // primer aviso ya pregunta si el panel esta abierto.
  let panelIsOpen = false;
  const emit = (): void => {
    for (const fn of listeners) fn();
    if (panelIsOpen) renderPanel();
  };

  const friendList = (): FriendEntry[] => friends.state()?.friends ?? [];
  const friendById = (id: string): FriendEntry | undefined => friendList().find((f) => f.clientId === id);

  const summary = (): SocialSummary => {
    const list = friendList();
    const here = list.filter((f) => f.status !== "offline");
    const state = friends.state();
    const view = party?.state() ?? null;
    const pending =
      friends.invites().length +
      (state?.incoming.length ?? 0) +
      (dms?.totalUnread() ?? 0) +
      (party?.invites().length ?? 0) +
      (party?.isLeader() ? (view?.requests?.length ?? 0) : 0);
    const partyInfo =
      party && party.inParty() && view
        ? {
            mates: party.mates(),
            leader: party.isLeader(),
            leaderId: view.leaderId,
            leaderName: view.members.find((m) => m.clientId === view.leaderId)?.nickname ?? "",
            max: view.max,
          }
        : null;
    let line = "";
    if (here.length > 0) {
      const playing = here.filter((f) => f.status === "playing").map((f) => f.nickname);
      const rest = here.filter((f) => f.status !== "playing").map((f) => f.nickname);
      const parts: string[] = [];
      if (playing.length > 0) parts.push(playing.length === 1 ? t("{a} está jugando", { a: playing[0]! }) : t("{a} están jugando", { a: namesText(playing) }));
      if (rest.length > 0) parts.push(rest.length === 1 ? t("{a} está conectado", { a: rest[0]! }) : t("{a} están conectados", { a: namesText(rest) }));
      line = parts.join(" · ");
    }
    // Con grupo, la tarjeta de Salas habla del grupo: es con quien vas a jugar.
    if (partyInfo) {
      const names = namesText(partyInfo.mates.map((m) => m.nickname));
      line = partyInfo.leader
        ? t("Tu grupo: {names}. Entra y jugáis juntos", { names })
        : t("Vas con el grupo de {nick}: donde entre, vas detrás", { nick: partyInfo.leaderName });
    }
    return { online: here.length, pending, line, party: partyInfo };
  };

  /**
   * "Invitar": al grupo si lo hay (como en Fortnite, primero se junta la
   * escuadra y luego el lider elige a que se juega); sin grupo, se monta una
   * sala con el.
   */
  const inviteAction = (clientId: string): BannerAction =>
    party
      ? { label: t("Invitar al grupo"), primary: true, run: () => party.invite(clientId) }
      : { label: t("Jugar"), primary: true, run: () => actions.playWith(clientId) };

  // --- quien se conecta ---
  let known: Map<string, FriendEntry["status"]> | null = null;
  const announcedAt = new Map<string, number>();
  const unsubState = friends.subscribe((state) => {
    if (state) {
      const now = Date.now();
      if (known) {
        const arrived = state.friends.filter(
          (f) => f.status !== "offline" && known!.get(f.clientId) === "offline" && now - (announcedAt.get(f.clientId) ?? 0) > ONLINE_REPEAT_MS,
        );
        for (const f of arrived) announcedAt.set(f.clientId, now);
        const where = opts.where();
        if (arrived.length > 0 && where !== "match") {
          if (arrived.length <= 2) {
            for (const f of arrived) {
              banner({
                key: `online:${f.clientId}`,
                icon: "🟢",
                html: t("{nick} se ha conectado", { nick: `<strong>${escapeHtml(f.nickname)}</strong>` }),
                actions: [inviteAction(f.clientId)],
                ms: 9000,
              });
            }
          } else {
            banner({
              key: "online:many",
              icon: "🟢",
              html: t("{n} amigos se han conectado", { n: arrived.length }),
              actions: [{ label: t("Ver"), primary: true, run: () => openPanel() }],
              ms: 9000,
            });
          }
        }
      }
      known = new Map(state.friends.map((f) => [f.clientId, f.status]));
    }
    emit();
  });

  // --- invitaciones, solicitudes, respuestas ---
  const deferred = new Set<string>();

  const inviteBanner = (inv: RoomInvite): void => {
    const seats = inv.humans !== undefined && inv.capacity ? ` · ${inv.humans}/${inv.capacity}` : "";
    banner({
      key: `invite:${inv.roomId}`,
      icon: "⚽",
      tone: "invite",
      html: `${t("{nick} te invita a jugar", { nick: `<strong>${escapeHtml(inv.from.nickname)}</strong>` })}<small>${escapeHtml(inv.roomName)} · ${escapeHtml(inv.modeId)}${seats}</small>`,
      actions: [
        { label: t("Unirse"), primary: true, run: () => actions.join(inv.roomId) },
        { label: t("Ahora no"), run: () => friends.declineInvite(inv.roomId) },
      ],
      ms: 30_000,
    });
  };

  const inviteResultText = (result: InviteResult, nick: string): string => {
    switch (result) {
      case "sent":
        return t("Invitación enviada a {nick}", { nick });
      case "playing":
        return t("{nick} está jugando: verá tu invitación al acabar", { nick });
      case "offline":
        return t("{nick} no está conectado. Avísale por otro lado", { nick });
      case "here":
        return t("{nick} ya está en tu sala", { nick });
      case "declined":
        return t("{nick} no puede ahora", { nick });
      case "slow":
        return t("Espera un poco antes de invitar a más gente");
    }
  };

  const unsubEvents = friends.onEvent((event) => {
    const where = opts.where();
    if (event.kind === "invite") {
      attention(t("{nick} te invita a jugar", { nick: event.invite.from.nickname }), where !== "match");
      if (where === "match") deferred.add(event.invite.roomId);
      else inviteBanner(event.invite);
    } else if (event.kind === "request") {
      attention(t("{nick} quiere ser tu amigo", { nick: event.from.nickname }), false);
      banner({
        key: `request:${event.from.clientId}`,
        icon: "👋",
        html: t("{nick} quiere ser tu amigo", { nick: `<strong>${escapeHtml(event.from.nickname)}</strong>` }),
        actions: [
          { label: t("Aceptar"), primary: true, run: () => friends.respond(event.from.clientId, true) },
          { label: t("Ahora no"), run: () => {} },
        ],
        ms: 20_000,
      });
    } else if (event.kind === "accepted") {
      banner({
        key: `accepted:${event.from.clientId}`,
        icon: "🤝",
        html: t("{nick} y tú ya sois amigos", { nick: `<strong>${escapeHtml(event.from.nickname)}</strong>` }),
        actions: [inviteAction(event.from.clientId)],
        ms: 9000,
      });
    } else if (event.kind === "inviteResult") {
      const nick = friendById(event.clientId)?.nickname ?? "";
      const bad = event.result === "offline" || event.result === "declined" || event.result === "slow";
      banner({
        key: `invres:${event.clientId}`,
        icon: event.result === "declined" ? "🙅" : event.result === "offline" ? "💤" : "📨",
        tone: bad ? "error" : "info",
        html: inviteResultText(event.result, `<strong>${escapeHtml(nick)}</strong>`),
        ...(event.result === "offline"
          ? { actions: [{ label: t("Avisar"), primary: true, run: () => void nudge(nick) }] }
          : {}),
        ms: event.result === "offline" ? 9000 : 5000,
      });
    } else if (event.kind === "result") {
      // Pedir amistad desde el lobby ya se contesta alli.
      if (where === "lobby" || where === "match") return;
      const text: Record<string, string> = {
        sent: t("Solicitud enviada"),
        accepted: t("¡Ya sois amigos!"),
        already: t("Ya sois amigos"),
        pending: t("Ya se la habías pedido"),
        not_found: t("No hay nadie con ese apodo o código"),
        limit: t("Límite de amigos alcanzado"),
        slow: t("Espera un poco antes de enviar más solicitudes"),
        blocked: t("Lo tienes bloqueado: desbloquéalo antes en Amigos"),
      };
      const msg = text[event.result];
      if (msg) notify(msg, ["not_found", "limit", "slow", "blocked"].includes(event.result) ? "error" : "info");
    }
    emit();
  });

  // --- el grupo ---
  /** El lider entro en una sala mientras jugabas: al acabar, el boton. */
  let deferredFollow: { roomId: string; from: string } | null = null;

  const partyInviteBanner = (inv: PartyInvite): void => {
    banner({
      key: `pinvite:${inv.partyId}`,
      icon: "⚑",
      tone: "invite",
      html: `${t("{nick} te invita a su grupo", { nick: `<strong>${escapeHtml(inv.from.nickname)}</strong>` })}<small>${escapeHtml(
        t("Iréis juntos a las salas y al mismo equipo"),
      )}</small>`,
      actions: [
        { label: t("Unirse"), primary: true, run: () => party?.join(inv.partyId) },
        { label: t("Ahora no"), run: () => party?.decline(inv.partyId) },
      ],
      ms: 30_000,
    });
  };

  const partyResultText = (result: PartyResult, nick: string): string => {
    switch (result) {
      case "sent":
        return t("Invitación al grupo enviada a {nick}", { nick });
      case "asked":
        return t("Le has pedido a {nick} entrar en su grupo", { nick });
      case "playing":
        return t("{nick} está jugando: verá tu invitación al acabar", { nick });
      case "offline":
        return t("{nick} no está conectado. Avísale por otro lado", { nick });
      case "here":
        return t("{nick} ya está en tu grupo", { nick });
      case "declined":
        return t("{nick} no puede ahora", { nick });
      case "full":
        return t("El grupo está lleno");
      case "slow":
        return t("Espera un poco antes de invitar a más gente");
    }
  };

  const unsubParty =
    party?.onEvent((event) => {
      const where = opts.where();
      if (event.kind === "invited") {
        attention(t("{nick} te invita a su grupo", { nick: event.invite.from.nickname }), where !== "match");
        if (where !== "match") partyInviteBanner(event.invite);
      } else if (event.kind === "requested") {
        attention(t("{nick} quiere unirse a tu grupo", { nick: event.from.nickname }), where !== "match");
        banner({
          key: `prequest:${event.from.clientId}`,
          icon: "✋",
          html: t("{nick} quiere unirse a tu grupo", { nick: `<strong>${escapeHtml(event.from.nickname)}</strong>` }),
          actions: [
            { label: t("Aceptar"), primary: true, run: () => party?.answer(event.from.clientId, true) },
            { label: t("No"), run: () => party?.answer(event.from.clientId, false) },
          ],
          ms: 20_000,
        });
      } else if (event.kind === "result") {
        const nick =
          friendById(event.clientId)?.nickname ?? party?.state()?.members.find((m) => m.clientId === event.clientId)?.nickname ?? "";
        const bad = event.result === "offline" || event.result === "declined" || event.result === "slow" || event.result === "full";
        banner({
          key: `pres:${event.clientId}`,
          icon: event.result === "declined" ? "🙅" : event.result === "offline" ? "💤" : "⚑",
          tone: bad ? "error" : "info",
          html: partyResultText(event.result, `<strong>${escapeHtml(nick)}</strong>`),
          ...(event.result === "offline" ? { actions: [{ label: t("Avisar"), primary: true, run: () => void nudge(nick) }] } : {}),
          ms: event.result === "offline" ? 9000 : 5000,
        });
      } else if (event.kind === "joined") {
        banner({ key: `pjoin:${event.who.clientId}`, icon: "⚑", html: t("{nick} se ha unido al grupo", { nick: `<strong>${escapeHtml(event.who.nickname)}</strong>` }), ms: 5000 });
      } else if (event.kind === "left") {
        banner({ key: `pleft:${event.who.clientId}`, icon: "👋", html: t("{nick} ha salido del grupo", { nick: `<strong>${escapeHtml(event.who.nickname)}</strong>` }), ms: 5000 });
      } else if (event.kind === "leader") {
        const mine = party?.isLeader() ?? false;
        banner({
          key: "pleader",
          icon: "👑",
          html: mine ? t("Ahora llevas tú el grupo") : t("{nick} lleva ahora el grupo", { nick: `<strong>${escapeHtml(event.who.nickname)}</strong>` }),
          ms: 5000,
        });
      } else if (event.kind === "gone") {
        if (event.reason === "kicked") notify(t("Te han sacado del grupo"), "error");
        else if (event.reason === "empty") notify(t("Tu grupo se ha deshecho: te has quedado solo"));
      } else if (event.kind === "follow") {
        // Jugando no se sale: al acabar, en los resultados, con su boton.
        if (where === "match") {
          deferredFollow = { roomId: event.roomId, from: event.from.nickname };
          return;
        }
        banner({
          key: "pfollow",
          icon: "⚑",
          html: t("Vas con tu grupo a la sala de {nick}", { nick: `<strong>${escapeHtml(event.from.nickname)}</strong>` }),
          ms: 4500,
        });
        actions.follow(event.roomId);
      }
      emit();
    }) ?? null;
  const unsubPartyState = party?.subscribe(() => emit()) ?? null;

  // --- privados, fuera del lobby ---
  const unsubDm =
    dms?.onIncoming((peer, entry) => {
      const where = opts.where();
      const nick = friendById(peer)?.nickname ?? "";
      attention(`✉ ${nick}`, false);
      if (where !== "menu" && where !== "results") return;
      banner({
        key: `dm:${peer}`,
        icon: "✉",
        html: `<strong>${escapeHtml(nick)}</strong>: ${escapeHtml(entry.text)}`,
        actions: [{ label: t("Responder"), primary: true, run: () => actions.openDm(peer) }],
        ms: 9000,
      });
    }) ?? null;
  const unsubDms = dms?.subscribe(() => emit()) ?? null;

  const notify = (text: string, tone: "info" | "error" = "info"): void => {
    banner({ key: `note:${text}`, icon: tone === "error" ? "⚠" : "✓", html: escapeHtml(text), tone, ms: 4500 });
  };

  const nudge = async (nickname: string): Promise<void> => {
    const outcome = await shareText(nudgeText());
    if (outcome === "copied") notify(t("Copiado. Pégaselo a {nick} por WhatsApp o donde habléis", { nick: nickname }));
    else if (outcome === "failed") notify(t("No se pudo copiar"), "error");
  };

  // ------------------------------------------------------------ panel
  const panel = document.createElement("div");
  panel.className = "sl-panel-layer is-hidden";
  panel.innerHTML = `
    <div class="sl-scrim" data-sl-close></div>
    <aside class="sl-panel" role="dialog" aria-modal="true" aria-label="${escapeHtml(t("Amigos"))}">
      <header class="sl-head">
        <h2>${escapeHtml(t("Amigos"))} <span data-sl-count></span></h2>
        <button type="button" class="sl-close" data-sl-close aria-label="${escapeHtml(t("Cerrar"))}">✕</button>
      </header>
      <div class="sl-body" data-sl-body></div>
      <footer class="sl-foot">
        <button type="button" class="sl-new" data-sl-new>＋ ${escapeHtml(t("Invitar a alguien que no juega"))}</button>
        <div class="sl-foot-row">
          <span class="sl-code">${escapeHtml(t("Tu código"))} <strong data-sl-code>····-····</strong></span>
          <button type="button" class="sl-mini" data-sl-copy>${escapeHtml(t("Copiar"))}</button>
          <button type="button" class="sl-mini" data-sl-sound></button>
        </div>
      </footer>
    </aside>`;
  root.appendChild(panel);
  const body = panel.querySelector("[data-sl-body]") as HTMLElement;
  const count = panel.querySelector("[data-sl-count]") as HTMLElement;
  const codeOut = panel.querySelector("[data-sl-code]") as HTMLElement;
  const soundBtn = panel.querySelector("[data-sl-sound]") as HTMLButtonElement;

  const syncSound = (): void => {
    soundBtn.textContent = soundOn() ? t("🔔 Avisos con sonido") : t("🔕 Avisos sin sonido");
    soundBtn.setAttribute("aria-pressed", String(soundOn()));
  };

  const hasInvite = (roomId: string): boolean => friends.invites().some((i) => i.roomId === roomId);

  /**
   * El boton que toca para cada amigo, y ninguno mas: decidir es cosa nuestra.
   * Por orden, como en Fortnite: si ya va contigo, nada; si tiene grupo, entrar
   * en el (o pedirlo); si esta en una sala abierta, ir; si esta conectado,
   * invitarle al tuyo; y si no esta, avisarle por fuera.
   */
  const actionFor = (f: FriendEntry): BannerAction | null => {
    if (party?.isMate(f.clientId)) return null;
    const mine = party?.state()?.id;
    if (party && f.status !== "offline" && f.party && f.party.size >= 2 && f.party.id !== mine) {
      const partyId = f.party.id;
      return f.party.open
        ? { label: t("Unirse al grupo"), primary: true, run: () => party.join(partyId) }
        : { label: t("Pedir unirse"), primary: true, run: () => party.request(partyId) };
    }
    if (f.status !== "offline" && f.roomId && f.roomOpen && (!f.roomLocked || hasInvite(f.roomId))) {
      return { label: t("Unirse"), primary: true, run: () => actions.join(f.roomId!) };
    }
    if (f.status !== "offline") return inviteAction(f.clientId);
    return { label: t("Avisar"), run: () => void nudge(f.nickname) };
  };

  /** Tu grupo, arriba del todo: con quien vas a jugar. */
  const renderParty = (): void => {
    const view = party?.state();
    if (!party || !view || view.members.length < 2) {
      const invites = party?.invites() ?? [];
      if (invites.length === 0) return;
      const ul = section(t("Te invitan a un grupo"));
      for (const inv of invites) {
        const li = document.createElement("li");
        li.className = "sl-row is-invite";
        li.innerHTML = `<span class="sl-row-info"><span class="sl-name">⚑ ${escapeHtml(inv.from.nickname)}</span><small>${escapeHtml(
          t("{n} en el grupo", { n: inv.size }),
        )}</small></span>`;
        li.append(
          button({ label: t("Unirse"), primary: true, run: () => party?.join(inv.partyId) }),
          button({ label: "✕", run: () => party?.decline(inv.partyId) }, "is-icon"),
        );
        ul.appendChild(li);
      }
      return;
    }
    const leader = party.isLeader();
    const box = document.createElement("section");
    box.className = "sl-party";
    box.innerHTML = `
      <header class="sl-party-head">
        <h3>${escapeHtml(t("Tu grupo"))} <span>${view.members.length}/${view.max}</span></h3>
        ${
          leader
            ? `<button type="button" class="sl-mini" data-party-open aria-pressed="${view.open}">${escapeHtml(
                view.open ? t("Abierto a amigos") : t("Solo con invitación"),
              )}</button>`
            : ""
        }
      </header>
      <p class="sl-party-hint">${escapeHtml(
        leader
          ? t("Tú eliges: entra en una sala o pulsa Jugar ya y tu grupo va detrás, en tu equipo.")
          : t("Lleva el grupo {nick}: donde entre, vas detrás.", {
              nick: view.members.find((m) => m.clientId === view.leaderId)?.nickname ?? "",
            }),
      )}</p>`;
    const ul = document.createElement("ul");
    ul.className = "sl-list";
    const statusText: Record<PartyMemberView["status"], string> = {
      online: t("En el menú"),
      room: t("En una sala"),
      playing: t("Jugando"),
      away: t("Reconectando…"),
    };
    for (const m of view.members) {
      const li = document.createElement("li");
      li.className = `sl-row is-${m.status === "away" ? "offline" : m.status}`;
      const crown = m.clientId === view.leaderId ? `<span class="sl-crown" title="${escapeHtml(t("Lleva el grupo"))}">👑</span>` : "";
      li.innerHTML = `
        <i class="sl-dot" aria-hidden="true"></i>
        <span class="sl-row-info">
          <span class="sl-name">${crown}${tierMarkHtml(m.tier)}${escapeHtml(m.nickname)}</span>
          <small>${escapeHtml(statusText[m.status])}</small>
        </span>`;
      // El lider, con los demas (consigo mismo no hay nada que hacer).
      if (leader && party.isMate(m.clientId)) {
        const promote = button({ label: "👑", run: () => party.promote(m.clientId) }, "is-icon");
        promote.title = t("Pasarle el mando");
        promote.setAttribute("aria-label", t("Pasarle el mando"));
        li.append(promote, button({ label: t("Echar"), run: () => party.kick(m.clientId) }, "is-icon"));
      }
      ul.appendChild(li);
    }
    for (const inv of view.invited) {
      const li = document.createElement("li");
      li.className = "sl-row is-pending";
      li.innerHTML = `<i class="sl-dot" aria-hidden="true"></i><span class="sl-row-info"><span class="sl-name">${escapeHtml(inv.nickname)}</span><small>${escapeHtml(
        t("Invitado, esperando respuesta…"),
      )}</small></span>`;
      ul.appendChild(li);
    }
    for (const r of view.requests ?? []) {
      const li = document.createElement("li");
      li.className = "sl-row is-invite";
      li.innerHTML = `<span class="sl-row-info"><span class="sl-name">✋ ${escapeHtml(r.nickname)}</span><small>${escapeHtml(t("Pide entrar"))}</small></span>`;
      li.append(
        button({ label: t("Aceptar"), primary: true, run: () => party.answer(r.clientId, true) }),
        button({ label: "✕", run: () => party.answer(r.clientId, false) }, "is-icon"),
      );
      ul.appendChild(li);
    }
    const leave = document.createElement("button");
    leave.type = "button";
    leave.className = "sl-mini sl-party-leave";
    leave.textContent = t("Salir del grupo");
    leave.addEventListener("click", () => party.leave());
    box.append(ul, leave);
    box.querySelector("[data-party-open]")?.addEventListener("click", () => party.setOpen(!view.open));
    body.appendChild(box);
  };

  const button = (a: BannerAction, cls = ""): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `sl-btn${a.primary ? " is-yes" : ""}${cls ? ` ${cls}` : ""}`;
    b.textContent = a.label;
    b.addEventListener("click", (ev) => {
      ev.stopPropagation();
      a.run();
    });
    return b;
  };

  const section = (title: string): HTMLUListElement => {
    const h = document.createElement("h3");
    h.className = "sl-section";
    h.textContent = title;
    const ul = document.createElement("ul");
    ul.className = "sl-list";
    body.append(h, ul);
    return ul;
  };

  const renderPanel = (): void => {
    const state = friends.state();
    const list = friendList();
    const here = list.filter((f) => f.status !== "offline");
    count.textContent = here.length > 0 ? t("· {n} conectados", { n: here.length }) : "";
    codeOut.textContent = state?.code ?? "····-····";
    syncSound();
    body.replaceChildren();

    if (!opts.online()) {
      const p = document.createElement("p");
      p.className = "sl-empty";
      p.textContent = t("Sin conexión. Tus amigos aparecen aquí en cuanto vuelva.");
      body.appendChild(p);
      return;
    }

    renderParty();

    const invites = friends.invites();
    if (invites.length > 0) {
      const ul = section(t("Te invitan"));
      for (const inv of invites) {
        const li = document.createElement("li");
        li.className = "sl-row is-invite";
        li.innerHTML = `<span class="sl-row-info"><span class="sl-name">⚽ ${escapeHtml(inv.from.nickname)}</span><small>${escapeHtml(inv.roomName)} · ${escapeHtml(inv.modeId)}</small></span>`;
        li.append(
          button({ label: t("Unirse"), primary: true, run: () => actions.join(inv.roomId) }),
          button({ label: "✕", run: () => friends.declineInvite(inv.roomId) }, "is-icon"),
        );
        ul.appendChild(li);
      }
    }

    if (state && state.incoming.length > 0) {
      const ul = section(t("Quieren ser tus amigos"));
      for (const r of state.incoming) {
        const li = document.createElement("li");
        li.className = "sl-row";
        li.innerHTML = `<span class="sl-row-info"><span class="sl-name">${escapeHtml(r.nickname)}</span></span>`;
        li.append(
          button({ label: t("Aceptar"), primary: true, run: () => friends.respond(r.clientId, true) }),
          button({ label: "✕", run: () => friends.respond(r.clientId, false) }, "is-icon"),
        );
        ul.appendChild(li);
      }
    }

    if (list.length === 0) {
      const p = document.createElement("p");
      p.className = "sl-empty";
      p.textContent = t("Aún no tienes amigos aquí. Pásale el enlace a alguien: al abrirlo, seréis amigos y estaréis en la misma sala.");
      body.appendChild(p);
      return;
    }

    const row = (f: FriendEntry): HTMLLIElement => {
      const li = document.createElement("li");
      li.className = `sl-row is-${f.status} is-clickable`;
      li.tabIndex = 0;
      li.setAttribute("role", "button");
      li.title = t("Ver su ficha");
      const chip = vsChip(f);
      const unread = dms?.unread(f.clientId) ?? 0;
      li.innerHTML = `
        <i class="sl-dot" aria-hidden="true"></i>
        <span class="sl-row-info">
          <span class="sl-name">${tierMarkHtml(f.tier)}${escapeHtml(f.nickname)}${
            chip ? ` <span class="sl-vs is-${chip.tone}" title="${escapeHtml(chip.title)}">⚔ ${chip.text}</span>` : ""
          }${unread > 0 ? ` <i class="sl-unread" title="${escapeHtml(t("{n} sin leer", { n: unread }))}">${unread > 9 ? "9+" : unread}</i>` : ""}</span>
          <small>${escapeHtml(whereText(f))}</small>
        </span>`;
      const open = (): void => actions.openCard(f.clientId, f.nickname);
      li.addEventListener("click", open);
      li.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          open();
        }
      });
      if (f.status !== "offline" || unread > 0) {
        li.appendChild(button({ label: "✉", run: () => actions.openDm(f.clientId) }, "is-icon"));
      }
      const act = actionFor(f);
      if (act) li.appendChild(button(act));
      else {
        const tag = document.createElement("span");
        tag.className = "sl-tag";
        tag.textContent = t("En tu grupo");
        li.appendChild(tag);
      }
      return li;
    };

    if (here.length > 0) {
      const ul = section(t("Conectados"));
      for (const f of here) ul.appendChild(row(f));
    }
    const away = list.filter((f) => f.status === "offline");
    if (away.length > 0) {
      const ul = section(t("Desconectados"));
      for (const f of away) ul.appendChild(row(f));
    }
  };

  const openPanel = (): void => {
    panelIsOpen = true;
    friends.refresh();
    renderPanel();
    panel.classList.remove("is-hidden");
    requestAnimationFrame(() => panel.classList.add("is-open"));
    (panel.querySelector(".sl-close") as HTMLElement).focus({ preventScroll: true });
  };
  const closePanel = (): void => {
    if (!panelIsOpen) return;
    panelIsOpen = false;
    panel.classList.remove("is-open");
    panel.classList.add("is-hidden");
  };
  for (const el of panel.querySelectorAll("[data-sl-close]")) el.addEventListener("click", closePanel);
  const onKey = (ev: KeyboardEvent): void => {
    if (panelIsOpen && ev.key === "Escape") {
      ev.preventDefault();
      ev.stopPropagation();
      closePanel();
    }
  };
  window.addEventListener("keydown", onKey, true);
  panel.querySelector("[data-sl-new]")!.addEventListener("click", () => {
    closePanel();
    actions.inviteNew();
  });
  panel.querySelector("[data-sl-copy]")!.addEventListener("click", () => {
    const code = friends.state()?.code;
    if (!code) return;
    void copyText(code).then((ok) => notify(ok ? t("Código copiado: {code}", { code }) : code));
  });
  soundBtn.addEventListener("click", () => {
    setSound(!soundOn());
    syncSound();
    if (soundOn()) {
      unlockAudio();
      ping();
    }
  });

  return {
    openPanel,
    closePanel,
    panelOpen: () => panelIsOpen,
    summary,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    flushDeferred() {
      const live = friends.invites().filter((i) => deferred.has(i.roomId));
      deferred.clear();
      for (const inv of live.reverse()) inviteBanner(inv);
      for (const inv of party?.invites() ?? []) partyInviteBanner(inv);
      if (deferredFollow) {
        const follow = deferredFollow;
        deferredFollow = null;
        banner({
          key: "pfollow",
          icon: "⚑",
          tone: "invite",
          html: t("Tu grupo está en la sala de {nick}", { nick: `<strong>${escapeHtml(follow.from)}</strong>` }),
          actions: [{ label: t("Ir con ellos"), primary: true, run: () => actions.follow(follow.roomId) }],
          ms: 30_000,
        });
      }
    },
    notify,
    dispose() {
      unsubState();
      unsubEvents();
      unsubDm?.();
      unsubDms?.();
      unsubParty?.();
      unsubPartyState?.();
      for (const id of timers.values()) window.clearTimeout(id);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointerdown", onVisibility, true);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", unlockAudio, true);
      window.removeEventListener("keydown", unlockAudio, true);
      stack.remove();
      panel.remove();
      listeners.clear();
    },
  };
}
